import API from "@/axios/API";
import CommonTable from "@/components/Common/Table";
import ApiRoundedIcon from "@mui/icons-material/ApiRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { THEME } from "./theme";

const MONITORING_BASE = `${import.meta.env.VITE_BACKEND_API || import.meta.env.BACKEND_API || ""}/auth/admin/monitoring`;
const PAGE_SIZE = 20;
const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "ANY"];

const fmtTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
};

const columns = [
    {
        key: "path",
        label: "Path",
        filterable: true,
        filterConfig: { heading: "Filter by Path", text: true, textPlaceholder: "e.g. /orders" },
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textPrimary, wordBreak: "break-all" },
        render: (r) => r.path,
    },
    {
        key: "method",
        label: "Method",
        filterable: true,
        filterConfig: { heading: "Filter by Method", chipsRadio: true, chips: METHODS.map((m) => ({ value: m, label: m })) },
        render: (r) => (
            <Chip
                size="small"
                label={r.method}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, fontFamily: THEME.mono, bgcolor: THEME.primary08, color: THEME.primary }}
            />
        ),
    },
    {
        key: "controllerClass",
        label: "Controller",
        cellSx: { fontSize: 12, color: THEME.textPrimary, whiteSpace: "nowrap" },
        render: (r) => r.controllerClass,
    },
    {
        key: "controllerMethod",
        label: "Method Name",
        cellSx: { fontSize: 12, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => r.controllerMethod,
    },
    {
        key: "firstSeen",
        label: "First Seen",
        filterable: true,
        filterConfig: {
            heading: "Sort by First Seen",
            sortOptions: [
                { dir: "DESC", label: "Newest first" },
                { dir: "ASC", label: "Oldest first" },
            ],
        },
        cellSx: { fontSize: 12, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => fmtTime(r.firstSeen),
    },
    {
        key: "lastSeen",
        label: "Last Seen",
        filterable: true,
        filterConfig: {
            heading: "Sort by Last Seen",
            sortOptions: [
                { dir: "DESC", label: "Newest first" },
                { dir: "ASC", label: "Oldest first" },
            ],
        },
        cellSx: { fontSize: 12, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => fmtTime(r.lastSeen),
    },
];

export default function RegistryTab() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState({ key: "lastSeen", dir: "DESC" });

    const fetchRegistry = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get(`${MONITORING_BASE}/registry`);
            setEntries(res.data ?? []);
        } catch (err) {
            setError(err.message || "Failed to load API registry.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRegistry();
    }, [fetchRegistry]);

    /* client-side filter + sort (registry is a small, permanent collection) */
    const filtered = useMemo(() => {
        let list = entries;

        const pathFilter = filters?.path?.text?.trim().toLowerCase();
        if (pathFilter) list = list.filter((e) => e.path?.toLowerCase().includes(pathFilter));

        const methodFilter = filters?.method?.chips?.[0];
        if (methodFilter) list = list.filter((e) => e.method === methodFilter);

        const sorted = [...list].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = String(av).localeCompare(String(bv));
            return sort.dir === "ASC" ? cmp : -cmp;
        });

        return sorted;
    }, [entries, filters, sort]);

    const pageData = useMemo(() => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE), [filtered, page]);

    const handleApplyFilter = useCallback((colKey, val) => {
        setFilters((prev) => ({ ...prev, [colKey]: val }));
        setPage(0);
    }, []);

    const handleClearFilter = useCallback((colKey) => {
        setFilters((prev) => {
            const next = { ...prev };
            delete next[colKey];
            return next;
        });
        setPage(0);
    }, []);

    const handleSort = useCallback((colKey, dir) => {
        setSort({ key: colKey, dir });
        setPage(0);
    }, []);

    const toolbarRight = (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: 12, color: THEME.textSecondary }}>{filtered.length} endpoints</Typography>
            <Tooltip title="Refresh" arrow>
                <IconButton size="small" onClick={fetchRegistry} sx={{ border: `1px solid ${THEME.border}`, color: THEME.textPrimary }}>
                    <RefreshRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );

    const emptyState = (
        <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <ApiRoundedIcon sx={{ fontSize: 36, color: THEME.textSecondary, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 13, color: THEME.textSecondary }}>No registered endpoints found.</Typography>
        </Box>
    );

    return (
        <Box>
            {error && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: `${THEME.radius.card}px`,
                        bgcolor: THEME.errorBg,
                        border: "1px solid #FFCDD2",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#C62828", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: "#B71C1C", flex: 1 }}>{error}</Typography>
                </Box>
            )}

            <CommonTable
                columns={columns}
                data={pageData}
                rowKey="id"
                loading={loading}
                skeletonRows={PAGE_SIZE}
                toolbarRight={toolbarRight}
                emptyState={emptyState}
                totalCount={filtered.length}
                page={page}
                rowsPerPage={PAGE_SIZE}
                onPageChange={(_, newPage) => setPage(newPage)}
                onApplyFilter={handleApplyFilter}
                onClearFilter={handleClearFilter}
                onSort={handleSort}
                filters={filters}
            />
        </Box>
    );
}
